import React, { useState, useEffect } from 'react';
import _path from 'ramda/src/path';
import PropTypes from 'prop-types';
import resx from '../../../utilities/resx';
import { LOGGLY_LEVEL_ERROR, RESX_REBLASTS, RESX_GLOBAL } from '../../../Constants';
import Share from '../Share';
import SaveLike from '../../../API/SaveLike';
import LogglyLogger from '../../../API/Loggly';
import { withMessage } from '../Message';

function ReblastTools(props) {
	const [newCommentCount, setNewCommentCount] = useState(0);
	const [pendingLike, setPendingLike] = useState(false);

	// initialize
	useEffect(() => {
		// subscribe to realtime events
		const handleNewComment = (e, data) => {
			// ignore if this was a comment you posted
			if (data.UserId === RESAAS.User2.UserId) {
				return;
			}
			// ignore if this is for a different reblast
			if (props.data.ReblastId !== data.ReblastGUID) {
				return;
			}
			// ignore if not new comment
			if (data.Event !== 'NewComment') {
				return;
			}
			// otherwise increment new comment count
			setNewCommentCount(prevCommentCount => prevCommentCount + 1);
		};

		$('body')
			.trigger('RSRealtimeSubscribe', [
				{
					channel: 'GlobalCommentListener',
					event: 'NewComment'
				}
			])
			.on('RSRealtimeNewComment', handleNewComment);

		// check session storage for like status
		if (window.sessionStorage) {
			const likeStatus = JSON.parse(window.sessionStorage.getItem(props.data.ReblastId));
			if (likeStatus && likeStatus.UserId === RESAAS.User2.UserId) {
				if (likeStatus.IsLikedByUser !== props.data.UserLikedReblast) {
					if (likeStatus.IsLikedByUser) {
						props.update(prevState => ({ ...prevState, UserLikedReblast: likeStatus.IsLikedByUser, ReblastLikeCounter: prevState.ReblastLikeCounter + 1 }));
					}
					else {
						props.update(prevState => ({ ...prevState, UserLikedReblast: likeStatus.IsLikedByUser, ReblastLikeCounter: prevState.ReblastLikeCounter - 1 }));
					}
				}
			}
		}

		// cleanup
		return function cleanup() {
			$('body').off('RSRealtimeNewComment', handleNewComment);
		};
	}, []);

	// click like 
	function clickLike() {
		if (pendingLike) {
			return;
		}
		const likedState = !props.data.UserLikedReblast;

		let criteria = { "ContentIdentifier": props.data.ReblastId, "ContentType": "1" };
		if (!likedState) {
			criteria={ ...criteria, "Delete": true };
		}

		setPendingLike(true);
		SaveLike.edit(criteria)
			.then((result) => {
				if (!_path(['data', 'SaveLikeResult', 'Success'], result)) {
					throw _path(['data', 'SaveLikeResult', 'Message'], result);
				}
				if (likedState) {
					props.update(prevState => ({ ...prevState, UserLikedReblast: likedState, ReblastLikeCounter: prevState.ReblastLikeCounter + 1 }));
				}
				else {
					props.update(prevState => ({ ...prevState, UserLikedReblast: likedState, ReblastLikeCounter: prevState.ReblastLikeCounter - 1 }));
				}

				if (window.sessionStorage) {
					window.sessionStorage.setItem(props.data.ReblastId, JSON.stringify({
						"IsLikedByUser": likedState,
						"UserId": RESAAS.User2.UserId
					}));
				}

			})
			.catch((error) => {
				props.message.addMessage({ message: resx(RESX_GLOBAL, 'GENERIC_WEBSERVICE_ERROR_MESSAGE'), type: 'error' });
				LogglyLogger.add(LOGGLY_LEVEL_ERROR, 'SaveLike failure to update like', error);
			})
			.finally(() => {
				setPendingLike(false);
			});
	}

	function clickComment() {
		// clear new comment count
		setNewCommentCount(0);

		// update state to expand comments commonent
		props.update(prevState => ({
			...prevState,
			commentsFetch: true,
			commentsDisplayCount: Infinity,
			commentsRequestedByUser: true,
			commentsPendingId: prevState.commentsPendingId + 1,
			hasMoreCommentsToShow: false
		}));
	}

	function renderLikeButton() {
		const likedClassName = props.data.UserLikedReblast ? "liked" : "";
		const likeText = props.data.UserLikedReblast ? resx(RESX_GLOBAL, 'LIKED') : resx(RESX_GLOBAL, 'LIKE');
		const isUser = _path(['User2', 'UserId'], RESAAS);
		if (!isUser) {
			return null;
		}
		return (
			<li className="re-reblast-actions-item re-like-component" onClick={clickLike}>
				<span className={`like-component ${likedClassName}`}>
					<a className="re-icon re-icon-like re-reblast-actions-button">{likeText}</a>
				</span>
			</li>
		);
	}

	function renderCommentButton() {
		if (!props.data.CanPostComments) {
			return null;
		}
		if (props.data.IsScrubbed) {
			return null;	
		}

		return (
			<li className="re-reblast-actions-item" onClick={clickComment}>
				<a className="js-show-commments-section re-reblast-actions-button">
					{newCommentCount < 1 ? <span>{resx(RESX_REBLASTS, 'COMMENT_TEXT')}</span> : ''}
					{newCommentCount === 1 ? <span className="notify-comment">{newCommentCount} {resx(RESX_REBLASTS, 'NEW_COMMENT')}</span> : ''}
					{newCommentCount > 1 ? <span className="notify-comment">{newCommentCount} {resx(RESX_REBLASTS, 'NEW_COMMENTS')}</span>: ''}
				</a>
			</li>
		);
	};

	// render share text
	function renderShareButton() {
		const firstName = _path(['data', 'User', 'FirstName'], props);
		const lastName = _path(['data', 'User', 'LastName'], props);
		const domain = _path(['RESAAS', 'Environment', 'ApplicationUrl'], window);
		const author = `${firstName} ${lastName}`;	
		const url = props.data.ListingSeoUrl ? props.data.ListingSeoUrl:`${domain}/reblast/${props.data.ReblastId}`;
		let type = "post";
	
		/***ReblastType
			Original = 0,
			Firm = 1,
			Group = 2,
			ReferralGroup = 3
		***/
		const reblastType = ['Firm', 'Group', 'ReferralGroup', 1, 2, 3];

		if (reblastType.indexOf(props.data.ReblastType) !== -1) {
			type = "privatepost";
		}
		else if (props.data.Listing) {
			type = "listingpost";
		}
		else if (props.data.HasImageInAttachment) {
			type = "imagepost";
		}
		return (
            <li className="re-reblast-actions-item">
                <Share shareUrl={url} contentType="post" showIconWithText={true} showDetails={true} shareMessage={`Check out the post on RESAAS!`} privacyType={type} />                      
			</li>
		);
	}

	return (
		<React.Fragment>
			<ul className="re-reblast-actions reblast-secondary-tools">
				{renderLikeButton()}
				{renderCommentButton()}
				{renderShareButton()}
			</ul>
		</React.Fragment>
	);
}

ReblastTools.propTypes = {
	message: PropTypes.object,
	data: PropTypes.object.isRequired,
	update: PropTypes.func.isRequired
};

export default withMessage(ReblastTools);
